import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Check, Loader2, Pencil, RefreshCw, Search, Undo2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/gestione/costi`;
const PAGINA = 30;
const euro = (v) => (v == null ? '—' : `${Number(v).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`);
const dataIt = (d) => (d ? d.split('-').reverse().join('/') : '');

const BADGE = {
  certo: ['Certo', 'bg-emerald-100 text-emerald-800'],
  confermato: ['Confermato', 'bg-blue-100 text-blue-800'],
  stimato: ['Stimato', 'bg-amber-100 text-amber-800'],
  sconosciuto: ['Da verificare', 'bg-red-100 text-red-700'],
};
const FILTRI = [['da_controllare', 'Da controllare'], ['usati', 'Usati nei piatti'], ['tutti', 'Tutti']];

const Prezzo = ({ p }) => (
  <p className="text-base font-bold text-gray-900" data-testid={`prezzo-${p.chiave}`}>
    {p.prezzo_kg != null ? `${euro(p.prezzo_kg)}/${p.unita === 'l' ? 'l' : 'kg'}` : `${euro(p.prezzo_pezzo)} a pezzo`}
  </p>
);

const Ultimo = ({ a }) => (a ? (
  <p className="text-xs text-gray-500">Ultimo: {a.quantita} × {euro(a.prezzo_unitario)} il {dataIt(a.data)} ({a.fornitore})</p>
) : null);

const Modifica = ({ p, onSalva, onChiudi, salvando }) => {
  const [kg, setKg] = useState(p.kg_unita ?? '');
  const [prezzo, setPrezzo] = useState('');
  return (
    <div className="mt-3 rounded-lg bg-gray-50 p-3 space-y-2" data-testid={`modifica-${p.chiave}`}>
      <label className="block text-xs text-gray-600">Peso di 1 unità in fattura (kg) — 0 se si compra a pezzo
        <Input type="number" step="0.001" min="0" value={kg} onChange={(e) => setKg(e.target.value)} className="mt-1 h-9" data-testid={`modifica-kg-${p.chiave}`} />
      </label>
      <label className="block text-xs text-gray-600">oppure prezzo al kg (€) se lo conosci
        <Input type="number" step="0.01" min="0" value={prezzo} onChange={(e) => setPrezzo(e.target.value)} className="mt-1 h-9" data-testid={`modifica-prezzo-${p.chiave}`} />
      </label>
      <div className="flex gap-2">
        <Button size="sm" variant="outline" onClick={onChiudi} data-testid={`modifica-annulla-${p.chiave}`}>Annulla</Button>
        <Button size="sm" className="bg-blue-600 hover:bg-blue-700" data-testid={`modifica-salva-${p.chiave}`} disabled={salvando}
          onClick={() => onSalva({ kg_unita: kg === '' ? null : Number(kg), prezzo_kg: prezzo === '' ? null : Number(prezzo) })}>
          {salvando && <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />} {salvando ? 'Ricalcolo i piatti…' : 'Salva'}
        </Button>
      </div>
    </div>
  );
};

const RigaProdotto = ({ p, onConferma, onAnnulla, occupato }) => {
  const [aperto, setAperto] = useState(false);
  const [lavoro, setLavoro] = useState(null);
  const [etichetta, colore] = BADGE[p.certezza] || BADGE.sconosciuto;
  const daControllare = ['stimato', 'sconosciuto'].includes(p.certezza);
  const esegui = async (tipo, fn) => { setLavoro(tipo); const ok = await fn(); setLavoro(null); return ok; };
  const salva = async (dati) => { if (await esegui('salva', () => onConferma(p, dati))) setAperto(false); };
  const icona = (tipo, Icona) => (lavoro === tipo ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Icona className="h-3.5 w-3.5 mr-1" />);
  return (
    <li className="py-3" data-testid={`prodotto-${p.chiave}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-gray-900 break-words">{p.descrizione}</p>
          <p className="text-xs text-gray-500">{p.fornitore}{p.piatti?.length ? ` · in ${p.piatti.length} piatti: ${p.piatti.slice(0, 2).join(', ')}${p.piatti.length > 2 ? '…' : ''}` : ''}</p>
          <p className="text-xs text-gray-600 mt-0.5">{p.come} · {p.finestra}</p>
          <Ultimo a={p.ultimo_acquisto} />
        </div>
        <div className="text-right shrink-0">
          <Prezzo p={p} />
          <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${colore}`} data-testid={`certezza-${p.chiave}`}>{etichetta}</span>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mt-2">
        {daControllare && (
          <Button size="sm" variant="outline" className="h-8 border-emerald-300 text-emerald-700" disabled={occupato}
            onClick={() => esegui('conferma', () => onConferma(p, { kg_unita: p.kg_unita ?? 0 }))} data-testid={`conferma-${p.chiave}`}>
            {icona('conferma', Check)} È giusto
          </Button>
        )}
        <Button size="sm" variant="outline" className="h-8" onClick={() => setAperto(!aperto)} data-testid={`cambia-${p.chiave}`}>
          <Pencil className="h-3.5 w-3.5 mr-1" /> Correggi
        </Button>
        {p.certezza === 'confermato' && (
          <Button size="sm" variant="ghost" className="h-8 text-gray-500" disabled={occupato} onClick={() => esegui('annulla', () => onAnnulla(p))} data-testid={`annulla-conferma-${p.chiave}`}>
            {icona('annulla', Undo2)} Torna alla stima
          </Button>
        )}
      </div>
      {aperto && <Modifica p={p} onSalva={salva} onChiudi={() => setAperto(false)} salvando={lavoro === 'salva'} />}
    </li>
  );
};

const Intestazione = ({ riepilogo, filtro, setFiltro, cerca, setCerca, onRicalcola, ricalcolo }) => (
  <div className="space-y-3">
    <div className="flex flex-wrap items-center justify-between gap-2">
      <div>
        <h2 className="text-base font-semibold text-gray-900">Prezzi dei prodotti</h2>
        <p className="text-xs text-gray-500" data-testid="prezzi-riepilogo">
          {riepilogo ? `${riepilogo.usati} prodotti usati nei piatti · ${riepilogo.da_controllare} da controllare` : 'Carico…'} · senza IVA, media degli ultimi 60 giorni
        </p>
      </div>
      <Button size="sm" variant="outline" onClick={onRicalcola} disabled={ricalcolo} data-testid="prezzi-ricalcola">
        {ricalcolo ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />} Ricalcola dalle fatture
      </Button>
    </div>
    <div className="flex flex-wrap gap-2">
      {FILTRI.map(([id, nome]) => (
        <button key={id} type="button" onClick={() => setFiltro(id)} data-testid={`prezzi-filtro-${id}`}
          className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${filtro === id ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
          {nome}
        </button>
      ))}
      <div className="relative flex-1 min-w-[180px]">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input value={cerca} onChange={(e) => setCerca(e.target.value)} placeholder="Cerca prodotto…" className="pl-8 h-8" data-testid="prezzi-cerca" />
      </div>
    </div>
  </div>
);

export default function PrezziProdotti({ headers }) {
  const [filtro, setFiltro] = useState('da_controllare');
  const [cerca, setCerca] = useState('');
  const [dati, setDati] = useState({ prodotti: [], totale: 0, riepilogo: null });
  const [caricamento, setCaricamento] = useState(true);
  const [occupato, setOccupato] = useState(false);

  const carica = useCallback(async (da = 0) => {
    setCaricamento(true);
    try {
      const { data } = await axios.get(`${API}/prodotti`, { headers: headers(), params: { filtro, cerca, da, n: PAGINA } });
      setDati((prima) => ({ ...data, prodotti: da ? [...prima.prodotti, ...data.prodotti] : data.prodotti }));
    } catch (e) {
      toast.error('Prezzi non disponibili');
    } finally {
      setCaricamento(false);
    }
  }, [filtro, cerca, headers]);

  useEffect(() => { const t = setTimeout(() => carica(0), 300); return () => clearTimeout(t); }, [carica]);

  const azione = async (fn, messaggio) => {
    setOccupato(true);
    try {
      await fn();
      toast.success(messaggio);
      await carica(0);
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Operazione non riuscita');
      return false;
    } finally {
      setOccupato(false);
    }
  };
  const conferma = (p, valori) => azione(() => axios.put(`${API}/prodotti`, { chiave: p.chiave, ...valori }, { headers: headers() }), 'Prezzo confermato: i piatti sono ricalcolati');
  const annulla = (p) => azione(() => axios.delete(`${API}/prodotti/conferma`, { headers: headers(), params: { chiave: p.chiave } }), 'Tornato alla stima dalle fatture');
  const ricalcola = () => azione(() => axios.post(`${API}/ricalcola`, {}, { headers: headers(), timeout: 120000 }), 'Prezzi ricalcolati dalle fatture');

  return (
    <div className="rounded-2xl bg-white border border-gray-200 p-4 space-y-3" data-testid="prezzi-prodotti">
      <Intestazione riepilogo={dati.riepilogo} filtro={filtro} setFiltro={setFiltro} cerca={cerca} setCerca={setCerca}
        onRicalcola={ricalcola} ricalcolo={occupato} />
      {caricamento && !dati.prodotti.length ? (
        <p className="flex items-center gap-2 text-sm text-gray-500 py-6" data-testid="prezzi-caricamento"><Loader2 className="h-4 w-4 animate-spin" /> Leggo le fatture…</p>
      ) : (
        <ul className="divide-y divide-gray-100" data-testid="prezzi-elenco">
          {dati.prodotti.map((p) => <RigaProdotto key={p.chiave} p={p} onConferma={conferma} onAnnulla={annulla} occupato={occupato} />)}
          {!dati.prodotti.length && <li className="py-6 text-sm text-gray-500" data-testid="prezzi-vuoto">Niente da controllare qui.</li>}
        </ul>
      )}
      {dati.prodotti.length < dati.totale && (
        <Button variant="ghost" className="w-full" onClick={() => carica(dati.prodotti.length)} disabled={caricamento} data-testid="prezzi-altri">
          Mostra altri ({dati.totale - dati.prodotti.length})
        </Button>
      )}
    </div>
  );
}
